// The whole visual identity lives here: nothing in this game is a straight line.
// Every stroke is jittered with a deterministic hash, and the jitter seed advances a few times a
// second, so the drawing "boils" the way a flipbook does.

export const PAPER = '#f7f3e6';
export const PAPER_DARK = '#ece5d2';
export const RULE = '#bcd0ee';
export const MARGIN = '#dd6f73';
export const INK = '#162a63';       // ballpoint blue, pressed hard
export const INK_SOFT = '#3d55a0';
export const PENCIL = '#33332f';    // graphite reads almost black on paper
export const PENCIL_SOFT = '#6a6a64';
export const RED = '#c22a2a';
export const GREEN = '#2a7340';
export const GOLD = '#c99024';
export const SKIN = '#e8c49c';
export const SKIN_LINE = '#9a6f46';
export const HIGHLIGHT = 'rgba(247, 221, 90, 0.45)';

/** Line hierarchy. A drawing reads as professional when the outline is heavier than the detail,
 *  and the shading is lighter than both. Everything in the game uses these three weights. */
export const W = { contour: 1.5, edge: 1.1, detail: 0.72, shade: 0.5 };

// Lines are hand-drawn but they hold still. Re-jittering every frame reads as trembling, which is
// tiring to look at for more than a minute; the wobble is opt-in with ?wobble=6 (redraws a second).
// Strokes must never fall below about a pixel on screen, or the page turns to grey mush when the
// view is zoomed out on a phone.
let inkScale = 1;
export const setInkScale = (z: number) => { inkScale = z; };
export const minWidth = (w: number) => Math.max(w, 0.42 / inkScale);

// On a phone every stroke costs real milliseconds, and a second half-transparent pass over a
// 1 px line reads as blur rather than as ink. Low detail draws once, with less wobble.
let lowDetail = false;
export const setLowDetail = (v: boolean) => { lowDetail = v; };
export const isLowDetail = () => lowDetail;

let wobbleHz = 0;
export const setWobble = (hz: number) => { wobbleHz = hz; };
let frameSeed = 0;
export const setInkFrame = (t: number) => { frameSeed = wobbleHz > 0 ? Math.floor(t * wobbleHz) : 0; };

export const hash = (a: number, b: number) => {
  let h = (a * 374761393 + b * 668265263 + frameSeed * 2147483647) >>> 0;
  h = (h ^ (h >>> 13)) * 1274126177 >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};
/** -1..1 wobble for point i of shape s */
const w1 = (s: number, i: number) => hash(s, i) * 2 - 1;

export interface StrokeOpts {
  color?: string; width?: number; amp?: number; seed?: number;
  /** draw the line twice with slightly different jitter, like a pen going over it again */
  double?: boolean;
  alpha?: number; dash?: number[]; cap?: CanvasLineCap;
}

export function inkLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, o: StrokeOpts = {}) {
  const seed = o.seed ?? 1, amp = (o.amp ?? 1.6) * (lowDetail ? 0.6 : 1);
  const len = Math.hypot(x2 - x1, y2 - y1);
  const steps = Math.max(2, Math.min(24, Math.round(len / 16)));
  ctx.save();
  ctx.strokeStyle = o.color ?? INK; ctx.lineWidth = minWidth(o.width ?? 1.7); ctx.lineCap = o.cap ?? 'round'; ctx.lineJoin = 'round';
  if (o.alpha !== undefined) ctx.globalAlpha = o.alpha;
  if (o.dash) ctx.setLineDash(o.dash);
  const pass = (k: number) => {
    ctx.beginPath();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const nx = -(y2 - y1) / (len || 1), ny = (x2 - x1) / (len || 1);
      const bow = Math.sin(t * Math.PI) * w1(seed + k * 91, 7) * amp * 0.9;
      const jx = w1(seed + k * 13, i) * amp * 0.5, jy = w1(seed + k * 29, i + 64) * amp * 0.5;
      const x = x1 + (x2 - x1) * t + nx * bow + jx;
      const y = y1 + (y2 - y1) * t + ny * bow + jy;
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.stroke();
  };
  pass(0);
  if (o.double && !lowDetail) { ctx.globalAlpha = (o.alpha ?? 1) * 0.7; ctx.lineWidth = minWidth((o.width ?? 1.7) * 0.55); pass(1); }
  ctx.restore();
}

/** closed or open path through points, each one nudged */
export function inkPath(ctx: CanvasRenderingContext2D, pts: [number, number][], o: StrokeOpts & { close?: boolean; fill?: string } = {}) {
  const seed = o.seed ?? 3, amp = (o.amp ?? 1.6) * (lowDetail ? 0.6 : 1);
  const jitter = pts.map(([x, y], i) => [x + w1(seed, i) * amp, y + w1(seed, i + 128) * amp] as [number, number]);
  ctx.save();
  ctx.lineWidth = minWidth(o.width ?? 1.7); ctx.strokeStyle = o.color ?? INK; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  if (o.alpha !== undefined) ctx.globalAlpha = o.alpha;
  const trace = () => {
    ctx.beginPath();
    jitter.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    if (o.close) ctx.closePath();
  };
  if (o.fill) { trace(); ctx.fillStyle = o.fill; ctx.fill(); }
  trace(); ctx.stroke();
  if (o.double && !lowDetail) { ctx.globalAlpha = (o.alpha ?? 1) * 0.65; ctx.lineWidth = minWidth((o.width ?? 1.7) * 0.55); ctx.beginPath(); pts.forEach(([x, y], i) => { const px = x + w1(seed + 77, i) * amp, py = y + w1(seed + 77, i + 128) * amp; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); }); if (o.close) ctx.closePath(); ctx.stroke(); }
  ctx.restore();
}

/** hand-drawn ellipse: a loop that does not quite meet */
export function inkEllipse(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, o: StrokeOpts & { fill?: string; overshoot?: number } = {}) {
  const seed = o.seed ?? 5, amp = o.amp ?? 1.5, over = o.overshoot ?? 0.35;
  const n = Math.max(lowDetail ? 8 : 10, Math.round((rx + ry) / (lowDetail ? 4.5 : 3)));
  const pts: [number, number][] = [];
  const start = w1(seed, 99) * 0.6;
  for (let i = 0; i <= n; i++) {
    const t = start + (i / n) * (Math.PI * 2 + over);
    pts.push([cx + Math.cos(t) * (rx + w1(seed, i) * amp), cy + Math.sin(t) * (ry + w1(seed, i + 50) * amp)]);
  }
  inkPath(ctx, pts, { ...o, close: false });
}

export function inkRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, o: StrokeOpts & { fill?: string; round?: number } = {}) {
  inkPath(ctx, [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], { ...o, close: true });
}

/** parallel pencil shading inside a box, at an angle */
export function hatch(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, o: StrokeOpts & { gap?: number; angle?: number } = {}) {
  const gap = (o.gap ?? 6) * (lowDetail ? 1.35 : 1), ang = o.angle ?? -0.7, seed = o.seed ?? 11;
  ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  const len = Math.hypot(w, h), dx = Math.cos(ang), dy = Math.sin(ang);
  const n = Math.ceil(len / gap) + 2;
  for (let i = -n; i < n; i++) {
    const ox = x + w / 2 + -dy * i * gap, oy = y + h / 2 + dx * i * gap;
    inkLine(ctx, ox - dx * len, oy - dy * len, ox + dx * len, oy + dy * len, { color: o.color ?? PENCIL_SOFT, width: o.width ?? 0.85, amp: 0.9, alpha: o.alpha ?? 0.42, seed: seed + i });
  }
  ctx.restore();
}

/** a splash of ink: one blob plus a few satellites */
export function inkBlot(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color = INK, seed = 17, alpha = 0.85) {
  ctx.save(); ctx.globalAlpha = alpha; ctx.fillStyle = color;
  const n = 11; ctx.beginPath();
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * Math.PI * 2;
    const rr = r * (0.72 + hash(seed, i) * 0.55);
    const x = cx + Math.cos(t) * rr, y = cy + Math.sin(t) * rr;
    i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
  }
  ctx.closePath(); ctx.fill();
  for (let i = 0; i < 4; i++) {
    const t = hash(seed + 5, i) * Math.PI * 2, d = r * (1.2 + hash(seed + 9, i) * 1.4);
    ctx.beginPath(); ctx.arc(cx + Math.cos(t) * d, cy + Math.sin(t) * d, r * (0.1 + hash(seed + 3, i) * 0.22), 0, 7); ctx.fill();
  }
  ctx.restore();
}

/** short motion ticks, the way a comic shows speed */
export function speedLines(ctx: CanvasRenderingContext2D, x: number, y: number, a: number, n = 3, len = 12, seed = 21) {
  for (let i = 0; i < n; i++) {
    const off = (i - (n - 1) / 2) * 6;
    const nx = -Math.sin(a), ny = Math.cos(a);
    inkLine(ctx, x + nx * off, y + ny * off, x - Math.cos(a) * len + nx * off, y - Math.sin(a) * len + ny * off,
      { color: PENCIL, width: 1.1, amp: 1.1, alpha: 0.5, seed: seed + i });
  }
}

// The page is the same every frame, only shifted: bake one strip of ruled paper and a separate
// sheet of shading, then scroll them. This alone is the difference between a phone keeping up and
// a phone giving up.
let stripCv: HTMLCanvasElement | null = null;
let stripKey = '';
let shadeCv: HTMLCanvasElement | null = null;
let shadeKey = '';

export function drawPaperFast(ctx: CanvasRenderingContext2D, camX: number, camY: number, vw: number, vh: number, zoom: number, dpr: number) {
  const step = 34 * zoom;
  const key = `${Math.round(vw)}x${Math.round(vh)}:${zoom.toFixed(2)}:${dpr}`;
  if (stripKey !== key || !stripCv) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(vw * dpr); cv.height = Math.ceil((vh + step * 2) * dpr);
    const c = cv.getContext('2d');
    if (!c) return drawPaper(ctx, camX, camY, vw, vh, zoom);
    c.scale(dpr, dpr);
    c.fillStyle = PAPER; c.fillRect(0, 0, vw, vh + step * 2);
    for (let y = 0; y < vh + step * 2; y += step) {
      inkLine(c, -10, y, vw + 10, y, { color: RULE, width: 1.05, amp: 0.5, seed: Math.round(y) % 977, alpha: 0.52 });
    }
    stripCv = cv; stripKey = key;
  }
  if (shadeKey !== key || !shadeCv) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(vw * dpr); cv.height = Math.ceil(vh * dpr);
    const c = cv.getContext('2d');
    if (c) {
      c.scale(dpr, dpr);
      const g = c.createLinearGradient(0, 0, vw, vh);
      g.addColorStop(0, 'rgba(255,255,255,0.5)'); g.addColorStop(0.55, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(120,100,60,0.09)');
      c.fillStyle = g; c.fillRect(0, 0, vw, vh);
      shadeCv = cv; shadeKey = key;
    }
  }
  const off = (((camY % 34) + 34) % 34) * zoom;
  ctx.drawImage(stripCv, 0, -off, vw, vh + step * 2);
  if (shadeCv) ctx.drawImage(shadeCv, 0, 0, vw, vh);
  // the red margin is one line, cheap enough to draw live
  const mx = (40 - camX) * zoom;
  if (mx > -40 && mx < vw + 40) {
    inkLine(ctx, mx, -10, mx, vh + 10, { color: MARGIN, width: 1.4, amp: 0.8, seed: 404, alpha: 0.5 });
    inkLine(ctx, mx + 3, -10, mx + 3, vh + 10, { color: MARGIN, width: 0.9, amp: 0.8, seed: 405, alpha: 0.2 });
  }
}

/** the ruled page itself, in world space so it scrolls with the camera */
export function drawPaper(ctx: CanvasRenderingContext2D, camX: number, camY: number, vw: number, vh: number, zoom: number) {
  ctx.fillStyle = PAPER; ctx.fillRect(0, 0, vw, vh);
  // the page is not perfectly flat: a soft warm shadow down the right and along the bottom
  const g = ctx.createLinearGradient(0, 0, vw, vh);
  g.addColorStop(0, 'rgba(255,255,255,0.5)'); g.addColorStop(0.55, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(120,100,60,0.09)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, vw, vh);
  const first = Math.floor(camY / 34) * 34;
  ctx.save();
  for (let wy = first; wy < camY + vh / zoom + 34; wy += 34) {
    const y = (wy - camY) * zoom;
    inkLine(ctx, -10, y, vw + 10, y, { color: RULE, width: 1.05, amp: 0.5, seed: (wy | 0) % 977, alpha: 0.52 });
  }
  const mx = (40 - camX) * zoom;
  if (mx > -40 && mx < vw + 40) {
    inkLine(ctx, mx, -10, mx, vh + 10, { color: MARGIN, width: 1.4, amp: 0.8, seed: 404, alpha: 0.5 });
    inkLine(ctx, mx + 3, -10, mx + 3, vh + 10, { color: MARGIN, width: 0.9, amp: 0.8, seed: 405, alpha: 0.2 });
  }
  ctx.restore();
}

/**
 * The shadow under an object, drawn the way a pencil draws one: short strokes angled away from
 * the light, packed near the contact point and breaking up towards the far edge. No gradients —
 * a blurred ellipse reads as digital compositing and fights the page.
 */
export function groundShade(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, seed: number, alpha = 0.16) {
  const rows = Math.max(3, Math.round(r / 5));
  const k = alpha / 0.16;
  for (let i = 0; i < rows; i++) {
    const t = i / rows;                       // 0 at the object, 1 at the far edge
    const ry = t * r * 0.44;
    const half = r * Math.sqrt(Math.max(0.02, 1 - t * t)) * 0.96;
    const strokes = Math.max(2, Math.round(half / 3.4));
    for (let j = 0; j < strokes; j++) {
      const f = strokes === 1 ? 0.5 : j / (strokes - 1);
      const sx = x - half + f * half * 2 + (hash(seed + i * 7, j) - 0.5) * 3;
      if (t > 0.45 && hash(seed + i, j + 40) > 0.75) continue;      // ragged far edge
      const len = 3.2 + hash(seed + i * 3, j) * 3.4;
      inkLine(ctx, sx, y + ry, sx + len * 0.75, y + ry + len * 0.32,
        { color: '#4a4a44', width: 0.75, amp: 0.35, alpha: (0.34 - t * 0.22) * k, seed: seed + i * 13 + j });
    }
  }
}

/** handwriting, slightly rotated so it never looks typeset */
export function penText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, o: { size?: number; color?: string; align?: CanvasTextAlign; seed?: number; font?: string; alpha?: number; tilt?: number } = {}) {
  const size = o.size ?? 16, seed = o.seed ?? 31;
  ctx.save();
  ctx.translate(x + w1(seed, 1) * 0.8, y + w1(seed, 2) * 0.8);
  ctx.rotate((o.tilt ?? w1(seed, 3) * 0.015));
  ctx.font = `${size}px ${o.font ?? FONT_HAND}`;
  ctx.textAlign = o.align ?? 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = o.color ?? INK; if (o.alpha !== undefined) ctx.globalAlpha = o.alpha;
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

export const FONT_HAND = '"Patrick Hand", "Segoe Print", "Bradley Hand", "Comic Sans MS", cursive';
export const FONT_PEN = '"Caveat", "Segoe Script", "Bradley Hand", cursive';

export function measureText(ctx: CanvasRenderingContext2D, text: string, size = 16, font = FONT_HAND) {
  ctx.save(); ctx.font = `${size}px ${font}`; const w = ctx.measureText(text).width; ctx.restore(); return w;
}

// ---------- colour ----------
// The page is drawn in ink, but it is *coloured* in pencil: a light flat pass for the local colour,
// directional hatching for the shadow side, and the ink contour left on top of both. Keeping the
// colour underneath the line is what separates a coloured drawing from a painted one.
export const C = {
  leaf: '#6d9a4e', leafDark: '#446f31', leafLight: '#93bb63',
  pine: '#4f7f4a', pineDark: '#335c33',
  trunk: '#9a7549', trunkDark: '#6b4e2e',
  roof: '#c2553d', roofDark: '#95382a',
  wall: '#f2ead7', wallDark: '#cdbd99',
  timber: '#8a6340',
  stone: '#b3b2a8', stoneDark: '#7f7e74',
  water: '#8fb6dd', waterDark: '#5580b4',
  grass: '#8cb257', grassDark: '#5d8438',
  earth: '#dbc79c', earthDark: '#bda274',
  cloth: '#c94f4f', clothBlue: '#7d9bc9',
  canvasWhite: '#fbf7ec',
};

export interface PencilOpts extends StrokeOpts {
  close?: boolean;
  /** the local colour, laid down flat and light */
  fill?: string; fillAlpha?: number;
  /** the shadow side, hatched */
  shade?: string; shadeAlpha?: number; gap?: number; angle?: number;
  /** skip the contour, for shapes that are only colour */
  noLine?: boolean;
  /** round the silhouette through the midpoints instead of cutting corners */
  smooth?: boolean;
}

/** a closed shape coloured the way a pencil colours it, with the ink outline on top */
export function pencil(ctx: CanvasRenderingContext2D, pts: [number, number][], o: PencilOpts = {}) {
  const seed = o.seed ?? 3, amp = (o.amp ?? 1.6) * (lowDetail ? 0.6 : 1);
  const jitter = pts.map(([x, y], i) => [x + w1(seed, i) * amp, y + w1(seed, i + 128) * amp] as [number, number]);
  // A closed silhouette drawn point to point shows its corners. Rounding through the midpoints is
  // what makes a boulder read as a boulder instead of as a cut-out polygon.
  const trace = () => {
    ctx.beginPath();
    if (o.smooth && jitter.length > 2) {
      const n = jitter.length;
      const mid = (i: number): [number, number] => {
        const a2 = jitter[i % n]!, b2 = jitter[(i + 1) % n]!;
        return [(a2[0] + b2[0]) / 2, (a2[1] + b2[1]) / 2];
      };
      const m0 = mid(n - 1);
      ctx.moveTo(m0[0], m0[1]);
      for (let i = 0; i < n; i++) { const c = jitter[i]!, m = mid(i); ctx.quadraticCurveTo(c[0], c[1], m[0], m[1]); }
    } else {
      jitter.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    }
    ctx.closePath();
  };
  if (o.fill) {
    ctx.save(); ctx.globalAlpha = o.fillAlpha ?? 0.55; ctx.fillStyle = o.fill; trace(); ctx.fill(); ctx.restore();
  }
  if (o.shade && !lowDetail) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const [x, y] of jitter) { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y; }
    ctx.save(); trace(); ctx.clip();
    hatch(ctx, x0, y0, x1 - x0, y1 - y0, { color: o.shade, alpha: o.shadeAlpha ?? 0.3, gap: o.gap ?? 5, angle: o.angle ?? -0.62, seed: seed + 41, width: 0.75 });
    ctx.restore();
  }
  if (!o.noLine) {
    if (o.smooth) {
      ctx.save();
      ctx.strokeStyle = o.color ?? INK; ctx.lineWidth = minWidth(o.width ?? 1.7);
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      if (o.alpha !== undefined) ctx.globalAlpha = o.alpha;
      trace(); ctx.stroke();
      if (o.double && !lowDetail) { ctx.globalAlpha = (o.alpha ?? 1) * 0.6; ctx.lineWidth = minWidth((o.width ?? 1.7) * 0.5); ctx.translate(0.5, 0.4); trace(); ctx.stroke(); }
      ctx.restore();
    } else inkPath(ctx, pts, { ...o, fill: undefined, close: true });
  }
}

/** the same treatment for a round shape — foliage, stones, pools */
export function pencilBlob(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, o: PencilOpts = {}) {
  const seed = o.seed ?? 5, amp = o.amp ?? 1.4;
  const n = lowDetail ? 10 : 14;
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const r = 0.82 + hash(seed, i) * 0.36;
    pts.push([cx + Math.cos(t) * rx * r, cy + Math.sin(t) * ry * r]);
  }
  pencil(ctx, pts, { ...o, amp, seed, smooth: o.smooth ?? true });
}

/**
 * A patch of colour laid down the way a pencil lays it: many short strokes, thinning out towards
 * the edge so the patch has no border. This is what keeps the ground from looking like a filled
 * polygon dropped on the page.
 */
export function scribble(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number,
  o: { color?: string; alpha?: number; n?: number; len?: number; angle?: number; seed?: number; width?: number } = {},
) {
  const seed = o.seed ?? 7, ang = o.angle ?? -0.55, base = o.alpha ?? 0.3;
  const n = Math.round((o.n ?? 22) * (lowDetail ? 0.5 : 1));
  const len = o.len ?? 11;
  for (let i = 0; i < n; i++) {
    const t = hash(seed, i) * Math.PI * 2, r = Math.sqrt(hash(seed, i + 311));
    const px = cx + Math.cos(t) * rx * r, py = cy + Math.sin(t) * ry * r;
    const a = ang + (hash(seed, i + 77) - 0.5) * 0.55;
    const l = len * (0.55 + hash(seed, i + 11) * 0.9);
    const dx = Math.cos(a) * l * 0.5, dy = Math.sin(a) * l * 0.5;
    const fade = base * (1 - r * 0.72) * (0.6 + hash(seed, i + 53) * 0.7);
    if (fade < 0.02) continue;
    inkLine(ctx, px - dx, py - dy, px + dx, py + dy,
      { color: o.color ?? GREEN, width: o.width ?? 0.85, amp: 0.55, alpha: fade, seed: seed + i * 3 });
  }
}
